<template>
  <g
    class="BarChart"
    :class="[
      `BarChart--${isHorizontal ? 'horizontal' : 'vertical'}`,
      `BarChart--${alignValue}Align`,
    ]"
    :transform="`translate(${marginLeft}, ${top})`"
  >
    <g
      ref="barChartContent"
      class="BarChart-content"
      :transform="`translate(${viewboxPosition.x + paddingX}, ${paddingY})`"
    >
      <template v-if="showBars">
        <g
          v-for="(bar, i) in bars"
          class="BarChart-bar"
          :transform="`translate(${bar[barXKey]}, ${bar[barYKey]})`"
          :key="i"
        >
          <template
            v-for="(child, j) in bar.children"
            :key="`bar-${i}-${j}-${child.fill}`"
          >
            <datalas-bar
              v-if="shadowOpacity && j == 0"
              class="Bar--bg"
              :style="{ opacity: shadowOpacity }"
              :key="`bg-${i}-${j}-${child.fill}`"
              :x="isHorizontal ? 0 - marginLeft - paddingX : child.bandPos"
              :y="isHorizontal ? child.bandPos : 0 - marginTop - paddingY"
              :width="
                isHorizontal
                  ? outerWidth + marginLeft + marginRight
                  : child.bandwidth
              "
              :height="
                isHorizontal
                  ? child.bandwidth
                  : outerHeight + marginTop + marginBottom
              "
              :is-negative="child.isNegative"
            />
            <RoundedBar
              :x="child[barXKey]"
              :y="child[barYKey]"
              :width="child[barWidthKey]"
              :height="child[barHeightKey]"
              :datum="child.datum"
              :is-negative="child.isNegative"
              :is-horizontal="isHorizontal"
              :radius="isRoundedBars"
              @datum:mouseover="$emit('datum:mouseover', $event)"
              @datum:mouseout="$emit('datum:mouseout', $event)"
              @datum:click="$emit('datum:click', $event)"
            />
          </template>
        </g>
      </template>

      <datalas-labels
        v-if="showLabels"
        :labels="labels"
        :prefix="labelPrefix"
        :suffix="labelSuffix"
        :format="labelFormat"
        :font-size="labelFontSize"
        :detect-collisions="noLabelCollisions"
        :filters="labelFilters"
        :offset-x="labelOffsetX"
        :offset-y="labelOffsetY"
        :angle="labelsAngle"
        :style="{
          pointerEvents: labelInside ? 'none' : undefined,
          'user-select': 'none',
        }"
      />
    </g>

    <teleport v-if="gridMounted" :to="`#${gridKey}`">
      <DatalasFlexibleGrid
        v-bind="gridLayout"
        :viewboxPosition="viewboxPosition"
        :left="marginLeft"
        :chartHeight="widgetContentWrapperRect.height"
        :grid-key="gridKey"
        @update:axisTickSize="$emit('update:axisTickSize', $event)"
        @update:axisSize="$emit('update:axisSize', $event)"
      />
    </teleport>
  </g>
</template>

<script>
import Bars from './bars.vue';
import RoundedBar from '../datum/rounded-bar.vue';
import DatalasFlexibleGrid from './flexible-grid.vue';

export default {
  name: 'DatalasFlexibleBars',
  components: { RoundedBar, DatalasFlexibleGrid },
  extends: Bars,
  props: {
    viewboxPosition: Object,
    default: () => ({ x: 0, y: 0 }),
    widgetContentWrapperRect: {
      type: Object,
      default: () => ({ width: 0, height: 0 }),
    },
    marginLeft: Number,
  },
  emits: [
    'update:axisTickSize',
    'update:layoutPaddings',
    'update:axisSize',
    'update:layoutTotalBandwidth',
  ],

  computed: {
    layoutTotalBandwidth() {
      if (this.isMobileWidth) {
        // step = band with + outer padding

        const padding = this.paddingX * 2;
        const margin = this.marginRight + this.marginLeft;
        const responsiveBandwidth =
          (this.widgetContentWrapperRect?.width - padding - margin) /
          this.nestedData.length;
        const minBandwidth = 40;

        const chartStep =
          responsiveBandwidth > minBandwidth
            ? responsiveBandwidth
            : minBandwidth;

        const projectedTotalBandwidth = this.nestedData.length * chartStep;

        const totalBandwith =
          projectedTotalBandwidth > this.widgetContentWrapperRect?.width
            ? projectedTotalBandwidth
            : this.totalBandwidth;

        this.$emit('update:layoutTotalBandwidth', totalBandwith);
        return totalBandwith;
      }

      this.$emit('update:layoutTotalBandwidth', this.totalBandwidth);
      return this.totalBandwidth;
    },
    gridLayout() {
      const grid = { ...this.grid };
      grid.axes = grid.axes.map((axis) => {
        const props = { ...axis.props };
        const { scale } = props;

        if (scale === this.length) {
          props.scale = this.lengthScale;
        } else {
          const { field } = this.transforms[scale];
          const layout = this.getFieldLayout(field);
          props.scale = this.makeOrdinalScale(layout);
          props.tickFormat = this.makeOrdinalFormatter(layout, axis);

          if (!props.hideDomainPath) {
            props.domainOffset = props.scale.copy()();
          }
        }

        switch (this.context.subtype) {
          case 'column':
            if (props.orient === 'bottom')
              props.labelAngle = this.isLabelIsLong(axis.props.scale, 10)
                ? props.labelAngle || -45
                : props.labelAngle;
            break;
          case 'row':
            if (props.orient === 'bottom') {
              props.labelAngle = this.isLabelIsLong(axis.props.scale, 5)
                ? props.labelAngle || -45
                : props.labelAngle;
            }
            break;
          default:
            break;
        }

        props.height = this.height;
        props.width = this.width + this.marginRight;
        return { ...axis, props };
      });

      return grid;
    },

    labelsAngle() {
      return this.context?.labelAngle || 0;
    },
    formatter() {
      return this.labelFormat ? this.$format(this.labelFormat) : (x) => x;
    },
    labels() {
      return this.bars.reduce(
        (acc, bar) => [
          ...acc,
          ...bar.children.map((child) => {
            const tempValue = child.datum[this.lengthTransform.field];

            const textAnchor = () => {
              const text = `${this.labelPrefix}${this.formatter(tempValue)}${
                this.labelSuffix
              }`;
              const labelLength = text.length * 8;

              const barHeight =
                this.height -
                (bar[this.barYKey] +
                  (child[this.barYKey] || 0) +
                  this.getLabelY(child[this.barHeightKey], child.isNegative));

              const defineTheLabelArea =
                barHeight + labelLength > this.height ? 'end' : 'start';
              const determineWhetherNecessaryMove = this.context.labelResponsive
                ? defineTheLabelArea
                : this.labelAnchorVal;

              return !this.labelInside && child.isNegative && this.isHorizontal
                ? 'end'
                : determineWhetherNecessaryMove;
            };

            return {
              // 1. Get X position of the outer bar (which may contain a stack)
              x:
                bar[this.barXKey] +
                // 2. Add the x-pos of the bar in the stack
                (child[this.barXKey] || 0) +
                // 3. Offset the label based on the size of the bar
                this.getLabelX(child[this.barWidthKey], child.isNegative),

              // Finding Y position is same process as x above, but flipped
              y:
                bar[this.barYKey] +
                (child[this.barYKey] || 0) +
                this.getLabelY(child[this.barHeightKey], child.isNegative),

              value: child.datum[this.lengthTransform.field],

              // On negative values for horizontal charts, always set
              // labelAnchor to 'end'
              textAnchor: textAnchor(),

              // Same for negative vertical charts, but with dominantBaseline:
              // 'hanging'
              dominantBaseline:
                !this.labelInside && child.isNegative && !this.isHorizontal
                  ? 'hanging'
                  : this.labelBaselineVal,

              datum: child.datum,
            };
          }),
        ],
        [],
      );
    },
    isRoundedBars() {
      return this.context?.roundedBars || 0;
    },
  },

  methods: {
    isLabelIsLong(value, numberOfCharacters) {
      if (this.transforms[value]) {
        const fieldName = this.transforms[value].field;
        const res = this.data.some(
          (item) => item[fieldName].toString().length >= numberOfCharacters,
        );
        return res;
      }
      return undefined;
    },
    getBarLayout(bar, bandwidth) {
      const items = bar.slice();
      const invert = this.isAlignedToFarEdge;
      const { field } = this.lengthTransform;

      const lengthScale = this.normalize
        ? this.lengthTransform.transformer(
            [this.min, this.getLengthSum(items)],
            this.range,
          )
        : this.lengthScale;
      const zero = lengthScale(0);

      // Note: this keeps the order of the items the same whether or not it's
      // flipped (align: left|right). Not sure if that's desired.
      // if (invert) items.reverse();

      // Stack items in bar
      return items.reduce((acc, datum, i) => {
        const val = datum[field];
        const isNegative = val < 0;
        const scaled = lengthScale(Math.abs(val));

        const edge = invert ? this.totalLength : 0;
        const baseline = Math[invert ? 'min' : 'max'](zero, edge);
        const length = invert ? baseline - scaled : scaled - baseline;

        const prev = acc[i - 1];
        const lengthStart = prev ? prev.lengthPos : baseline;
        const prevLength = prev ? prev.length : 0;

        const pos = isNegative ? zero - length : lengthStart + prevLength;
        const inversePos = isNegative ? zero : lengthStart - length;
        const lengthPos = invert ? inversePos : pos;

        const fillKey = datum[this.fillTransform.field];
        const fill = this.fillScale(fillKey);

        return acc.concat([
          {
            lengthPos,
            bandwidth,
            length,
            datum: { ...datum, fill },
            isNegative,
          },
        ]);
      }, []);
    },
  },
  mounted() {
    const paddings = {
      top: this.top,
      right: this.right,
      bottom: this.bottom,
      left: this.left,
    };

    if (paddings) this.$emit('update:layoutPaddings', paddings);
  },
};
</script>
